import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  DeleteOutlined as DeleteOutlineIcon,
} from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import {
  fetchRequisitionDetails,
  updateExistingRequisition,
  fetchRequisitionMasterItems,
} from "store/features/company/requisition/requisitionActions";
import {
  selectRequisitionDetails,
  selectRequisitionDetailsLoading,
} from "store/features/company/requisition/requisitionSelectors";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";

const fmtINR = (n) =>
  "₹ " +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const EditRequisitionModal = ({ open, onClose, requisitionId, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";

  const projects = useSelector(selectProjects) || [];
  const departments = useSelector(selectDepartments) || [];
  const requisitionDetailsMap = useSelector(selectRequisitionDetails) || {};
  const detailsLoadingMap = useSelector(selectRequisitionDetailsLoading) || {};

  const currentDetails = requisitionId ? requisitionDetailsMap[requisitionId] : null;
  const loadingDetails = requisitionId ? detailsLoadingMap[requisitionId] : false;

  const { hasPermission, hasAnyPermission } = usePermissions();
  const currentUser = useSelector(selectCurrentUser);
  const userRole = currentUser?.role || currentUser?.user_type;

  // const canEdit = hasAny(hasPermission, ["requisition.create", "requisition.update"]) || userRole === "Company";
  // const canDelete = hasAny(hasPermission, ["requisition.delete", "requisitions.delete", "requisition.create"]) || userRole === "Company";

  const [purposeType, setPurposeType] = useState("PROJECT");
  const [meta, setMeta] = useState({
    project_id: "",
    department_id: "",
    description: "",
    requested_by: "",
  });

  const [items, setItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (projects.length === 0) dispatch(fetchProjects());
    if (departments.length === 0) dispatch(fetchDepartments());
  }, [dispatch, projects.length, departments.length]);

  useEffect(() => {
    if (open && requisitionId) {
      dispatch(fetchRequisitionDetails(requisitionId)).then((res) => {
        const detail = res?.data || currentDetails;
        if (detail) {
          populateForm(detail);
        }
      });
    }
  }, [open, requisitionId, dispatch]);

  useEffect(() => {
    if (currentDetails) {
      populateForm(currentDetails);
    }
  }, [currentDetails]);

  const populateForm = (detail) => {
    if (!detail) return;
    setPurposeType(detail.requisition_type || "PROJECT");
    setMeta({
      project_id: detail.project_id ? String(detail.project_id) : "",
      department_id: detail.department_id ? String(detail.department_id) : "",
      description: detail.description || "",
      requested_by: detail.requested_by ? String(detail.requested_by) : "",
    });

    if (Array.isArray(detail.items) && detail.items.length > 0) {
      setItems(
        detail.items.map((item, idx) => ({
          id: item.id || Date.now() + idx,
          item_name: item.item_name || "",
          item_description: item.item_description || "",
          quantity: item.quantity || 1,
          unit_rate: item.unit_rate || 0,
          amount: item.amount || (item.quantity || 1) * (item.unit_rate || 0),
          required_by: item.required_by || "",
        }))
      );
    } else {
      setItems([
        {
          id: Date.now(),
          item_name: "",
          item_description: "",
          quantity: 1,
          unit_rate: 0,
          amount: 0,
          required_by: "",
        },
      ]);
    }
  };


  useEffect(() => {
    if (!open) return;
    const fetchMaster = async () => {
      let projectId = null;
      if (purposeType === "PROJECT" && meta.project_id) {
        projectId = meta.project_id;
      }
      const res = await dispatch(fetchRequisitionMasterItems(projectId));
      if (res.success) {
        setMasterItems(res.data || []);
      }
    };
    fetchMaster();
  }, [open, purposeType, meta.project_id, dispatch]);

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        item_name: "",
        item_description: "",
        quantity: 1,
        unit_rate: 0,
        amount: 0,
        required_by: "",
      },
    ]);
  };

  const handleRemoveItem = (id) => {
    if (items.length <= 1) {
      showErrorToast("At least one line item is required");
      return;
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleItemChange = (id, field, val) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: val };
        if (field === "quantity" || field === "unit_rate") {
          const q = parseFloat(field === "quantity" ? val : item.quantity) || 0;
          const r = parseFloat(field === "unit_rate" ? val : item.unit_rate) || 0;
          updated.amount = q * r;
        }
        return updated;
      })
    );
  };

  const totalCalculatedAmount = items.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );

  const handleSubmit = async () => {
    if (purposeType === "PROJECT" && !meta.project_id) {
      showErrorToast("Please select a project");
      return;
    }
    if (purposeType === "COMPANY" && !meta.department_id) {
      showErrorToast("Please select a department");
      return;
    }

    if (items.length === 0) {
      showErrorToast("At least one line item is required");
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.item_name.trim()) {
        showErrorToast(`Item #${i + 1}: Name is required`);
        return;
      }
      if (parseFloat(it.quantity) <= 0) {
        showErrorToast(`Item #${i + 1}: Quantity must be greater than 0`);
        return;
      }
      if (parseFloat(it.unit_rate) <= 0) {
        showErrorToast(`Item #${i + 1}: Unit rate must be greater than 0`);
        return;
      }
    }

    const payload = {
      requisition_type: purposeType,
      project_id: purposeType === "PROJECT" ? parseInt(meta.project_id) : null,
      department_id: purposeType === "COMPANY" ? parseInt(meta.department_id) : null,
      requested_by: meta.requested_by ? parseInt(meta.requested_by) : currentDetails?.requested_by,
      description: meta.description,
      items: items.map((it) => ({
        item_name: it.item_name.trim(),
        item_description: it.item_description,
        quantity: parseFloat(it.quantity),
        unit_rate: parseFloat(it.unit_rate),
        amount: parseFloat(it.amount),
        required_by: it.required_by || null,
      })),
    };

    setIsSubmitting(true);
    const result = await dispatch(updateExistingRequisition(requisitionId, payload));
    setIsSubmitting(false);

    if (result?.success) {
      if (onSuccess) onSuccess();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: isDark ? "#0f172a" : "#ffffff",
          color: isDark ? "#f3f4f6" : "#111827",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
          py: 2.5,
          px: 3.5,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Edit Requisition {currentDetails?.requisition_no ? `#${currentDetails.requisition_no}` : ""}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5 }}>
        {loadingDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Stack spacing={3.5} sx={{ mt: 1.5 }}>
            {/* Header Form */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <TextField
                select
                label="Requisition Purpose"
                value={purposeType}
                onChange={(e) => setPurposeType(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="PROJECT">Project Specific</MenuItem>
                <MenuItem value="COMPANY">Company / Internal</MenuItem>
              </TextField>

              {purposeType === "PROJECT" ? (
                <TextField
                  select
                  label="Select Project"
                  value={meta.project_id}
                  onChange={(e) => setMeta({ ...meta, project_id: e.target.value })}
                  fullWidth
                  size="small"
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  select
                  label="Select Department"
                  value={meta.department_id}
                  onChange={(e) => setMeta({ ...meta, department_id: e.target.value })}
                  fullWidth
                  size="small"
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

            </Stack>

            <TextField
              label="Requisition Description"
              multiline
              rows={2}
              value={meta.description}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })}
              fullWidth
              size="small"
              placeholder="Briefly explain the reason for this requisition"
            />

            <Divider sx={{ my: 1.5 }} />

            {/* Line Items Table */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Line Items ({items.length})
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={handleAddItem}
                  sx={{ borderRadius: "8px", textTransform: "none", px: 2, ml: 2, mb: 1, }}
                >
                  Add Item
                </Button>
              </Stack>

              <Table
                size="small"
                sx={{
                  border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
                  // borderRadius: "16px",
                  borderCollapse: "separate",
                  "& td, & th": {
                    px: 2,
                    py: 1.5,
                  },
                }}
              >
                <TableHead sx={{ backgroundColor: isDark ? "#1e293b" : "#f9fafb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "100px" }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "120px" }}>Unit Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "120px" }}>Amount</TableCell>
                    <TableCell sx={{ width: "50px" }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Autocomplete
                          freeSolo
                          options={masterItems.map((m) => m.item_name)}
                          value={item.item_name}
                          onInputChange={(e, val) => handleItemChange(item.id, "item_name", val)}
                          renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Item name" variant="outlined" />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={item.item_description}
                          onChange={(e) => handleItemChange(item.id, "item_description", e.target.value)}
                          placeholder="Spec / Notes"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, "quantity", e.target.value)}
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.unit_rate}
                          onChange={(e) => handleItemChange(item.id, "unit_rate", e.target.value)}
                          inputProps={{ min: 0, step: "any" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} color="#10b981" fontSize="0.875rem">
                          {fmtINR(item.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={items.length <= 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Total Estimated Amount
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#10b981">
                {fmtINR(totalCalculatedAmount)}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1.5, borderTop: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}` }}>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting} sx={{ px: 2.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || loadingDetails}
          sx={{
            borderRadius: "8px",
            backgroundColor: "#2563eb",
            fontWeight: 700,
            px: 3,
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRequisitionModal;